import {
	Balloon,
	BalloonGenerationJob,
	BalloonGenerationOptions,
	BallooningProject,
	BallooningSettings,
	ProductionMethodKeyence,
	CreateBalloon,
	CreateBallooningProject,
	ReferenceBallooningProject,
	TraceViewsResponse,
} from '../../interfaces/Ballooning';
import { MeasurementEdit } from '../../utils/balloonReview';
import { api } from '../slices/api';

export interface BallooningProjectsPage {
	results: BallooningProject[];
	count: number;
	pageSize: number;
}

/**
 * Normalise the ballooning project list response.
 *
 * The list is paginated server-side (StandardResultsSetPagination, 30 per page)
 * because the unpaginated list OOM-killed fastlane-api (PTB-6878), so the body is
 * `{count, next, previous, page_size, results}`.
 *
 * A bare array is accepted too, deliberately: frontend and backend deploy
 * separately, so this has to render against the old response as well as the new
 * one, in either deploy order.
 */
export const unwrapBallooningProjects = (
	response:
		| BallooningProject[]
		| { results?: BallooningProject[]; count?: number; page_size?: number }
		| null
		| undefined,
): BallooningProjectsPage => {
	if (Array.isArray(response)) {
		return { results: response, count: response.length, pageSize: response.length || 1 };
	}
	const results = response?.results ?? [];
	return {
		results,
		count: response?.count ?? results.length,
		pageSize: response?.page_size ?? (results.length || 1),
	};
};

// Define a service using a base URL and expected endpoints
export const extendedApiSlice = api.injectEndpoints({
	endpoints: (builder) => ({
		getBallooningProjects: builder.query<BallooningProjectsPage, { search?: string; page?: number }>({
			query: (params) => ({
				url: `ballooning/projects/`,
				method: 'GET',
				params,
			}),
			// Paginated server-side (PTB-6878); see unwrapBallooningProjects for why
			// both shapes are accepted.
			transformResponse: unwrapBallooningProjects,
		}),
		getBallooningProject: builder.query({
			query: ({ id }: { id: string }) => ({
				url: `ballooning/projects/${id}`,
				method: 'GET',
			}),
		}),
		getBallooningProjectByLineItem: builder.query({
			query: ({ id }: { id: string }) => ({
				url: `ballooning/projects/by-line-item/${id}`,
				method: 'GET',
			}),
		}),
		exportBallooningProjectToImage: builder.query({
			query: ({ id }: { id: string }) => ({
				url: `ballooning/projects/${id}/export/`,
				method: 'GET',
			}),
		}),
		updateBallooningProject: builder.mutation({
			query: ({ id, body }: { id: string; body: any }) => ({
				url: `ballooning/projects/${id}/`,
				method: 'PATCH',
				body: body,
			}),
			invalidatesTags: ['InspectionPlan', 'InspectionSample', 'OrderLineItem'],
		}),
		getBalloonsByProject: builder.query({
			query: ({ id }: { id: string }) => ({
				url: `ballooning/projects/${id}/balloons`,
				method: 'GET',
			}),
		}),
		createBallooningProject: builder.mutation({
			query: ({ body }: { body: CreateBallooningProject }) => ({
				url: `ballooning/projects/`,
				method: 'POST',
				body: body,
			}),
		}),
		createBalloon: builder.mutation({
			query: ({ body }: { body: CreateBalloon }) => ({
				url: `ballooning/balloon/`,
				method: 'POST',
				body: body,
			}),
		}),
		updateBalloon: builder.mutation({
			query: ({ id, body }: { id: string; body: Balloon }) => ({
				url: `ballooning/balloon/${id}/`,
				method: 'PATCH',
				body: body,
			}),
		}),
		deleteBalloon: builder.mutation({
			query: ({ id }: { id: string }) => ({
				url: `ballooning/balloon/${id}/`,
				method: 'DELETE',
			}),
		}),
		setupInspectionPlan: builder.mutation({
			// The endpoint answers 409 with `confirmation_required` when the balloons were
			// imported from a part that is not matched to this line item; resend with the
			// acknowledgement once the operator confirms they checked the values.
			query: ({
				id,
				acknowledgeUnverifiedReuse,
			}: {
				id: string;
				acknowledgeUnverifiedReuse?: boolean;
			}) => ({
				url: `ballooning/projects/${id}/setup/`,
				method: 'POST',
				...(acknowledgeUnverifiedReuse
					? { body: { acknowledge_unverified_reuse: true } }
					: {}),
			}),
		}),
		resetInspectionPlan:builder.mutation({
			query: ({ id}: { id: string; }) => ({
				url: `ballooning/projects/${id}/reset/`,
				method: 'POST',
			  }),
		}),
		validateBallooningChanges: builder.mutation({
			query: ({ id, body }: { id: string; body: any }) => ({
				url: `ballooning/projects/${id}/validate-changes/`,
				method: 'POST',
				body: body,
			}),
		}),
		// Free search for a reuse source. Separate from previous_ballooning_projects on
		// purpose: that list is limited to partsbadger_api matches, while this can return
		// any part and therefore counts as an unverified source.
		searchReferenceProjects: builder.query<
			{ results: ReferenceBallooningProject[] },
			{ q: string; exclude?: string; limit?: number }
		>({
			query: ({ q, exclude, limit }) => ({
				url: `ballooning/projects/reference-search/`,
				method: 'GET',
				params: { q, ...(exclude ? { exclude } : {}), ...(limit ? { limit } : {}) },
			}),
		}),
		refreshPreviousOrders: builder.mutation<
			{
				detail: string;
				queued: boolean;
			},
			{ id: string }
		>({
			query: ({ id }) => ({
				url: `ballooning/projects/${id}/refresh-previous-orders/`,
				method: 'POST',
			}),
		}),
		// Queues AI auto-ballooning; answers 409 when the project already has balloons,
		// is locked, or a job is already running.
		generateBalloons: builder.mutation<
			BalloonGenerationJob,
			{ id: string; options?: BalloonGenerationOptions }
		>({
			query: ({ id, options }) => ({
				url: `ballooning/projects/${id}/generate-balloons/`,
				method: 'POST',
				body: options ?? {},
			}),
		}),
		// Stops the project's running AI analysis; 409 when nothing is running.
		cancelGeneration: builder.mutation<BalloonGenerationJob, { id: string }>({
			query: ({ id }) => ({
				url: `ballooning/projects/${id}/cancel-generation/`,
				method: 'POST',
			}),
		}),
		// Patches the cached project instead of refetching it, which would reset unsaved balloon edits.
		traceViews: builder.mutation<TraceViewsResponse, { id: string }>({
			query: ({ id }) => ({
				url: `ballooning/projects/${id}/trace-views/`,
				method: 'POST',
			}),
			async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
				let data: TraceViewsResponse;
				try {
					({ data } = await queryFulfilled);
				} catch {
					return;
				}
				const outlines = new Map(data.pages.map((p) => [p.id, p.view_outlines]));
				dispatch(
					extendedApiSlice.util.updateQueryData('getBallooningProject', { id }, (draft: BallooningProject) => {
						draft.ballooning_pages?.forEach((page) => {
							if (outlines.has(page.id)) page.view_outlines = outlines.get(page.id);
						});
					})
				);
			},
		}),
		// Latest generation job for the project, or null when none ever ran.
		getGenerationJob: builder.query<BalloonGenerationJob | null, { id: string }>({
			query: ({ id }) => ({
				url: `ballooning/projects/${id}/generation-job/`,
				method: 'GET',
			}),
		}),
		// Saves the approved balloons' measurement edits too.
		approveBalloons: builder.mutation<
			Balloon[],
			{ projectId: string; ids: string[]; edits?: MeasurementEdit[] }
		>({
			query: ({ projectId, ...body }) => ({
				url: `ballooning/projects/${projectId}/approve-balloons/`,
				method: 'POST',
				body,
			}),
		}),
		// Development only (the backend answers 404 elsewhere): replaces the project's
		// pages with an uploaded PDF or image. 409 when balloons exist unless
		// replace_balloons is set.
		devUploadDrawing: builder.mutation<
			{ id: string; page_count: number },
			{ id: string; file: File; replaceBalloons?: boolean }
		>({
			query: ({ id, file, replaceBalloons }) => {
				const formData = new FormData();
				formData.append('file', file);
				if (replaceBalloons) formData.append('replace_balloons', 'true');
				return {
					url: `ballooning/projects/${id}/dev-upload-drawing/`,
					method: 'POST',
					body: formData,
					timeout: 300000,
				};
			},
		}),
		getKeyenceByProductionMethod: builder.query<ProductionMethodKeyence[], void>({
			query: () => 'ballooning/keyence-by-production-method/',
			providesTags: ['KeyenceByProductionMethod'],
		}),
		getProductionMethods: builder.query<string[], void>({
			query: () => 'ballooning/keyence-by-production-method/production-methods/',
		}),
		createKeyenceByProductionMethod: builder.mutation<
			ProductionMethodKeyence,
			Omit<ProductionMethodKeyence, 'id' | 'keyence_device_name'>
		>({
			query: (body) => ({
				url: 'ballooning/keyence-by-production-method/',
				method: 'POST',
				body,
			}),
			invalidatesTags: ['KeyenceByProductionMethod'],
		}),
		updateKeyenceByProductionMethod: builder.mutation<
			ProductionMethodKeyence,
			Pick<ProductionMethodKeyence, 'id'> &
				Partial<Omit<ProductionMethodKeyence, 'id' | 'keyence_device_name'>>
		>({
			query: ({ id, ...body }) => ({
				url: `ballooning/keyence-by-production-method/${id}/`,
				method: 'PATCH',
				body,
			}),
			invalidatesTags: ['KeyenceByProductionMethod'],
		}),
		deleteKeyenceByProductionMethod: builder.mutation<void, { id: string }>({
			query: ({ id }) => ({
				url: `ballooning/keyence-by-production-method/${id}/`,
				method: 'DELETE',
			}),
			invalidatesTags: ['KeyenceByProductionMethod'],
		}),
		getBallooningSettings: builder.query<BallooningSettings, void>({
			query: () => 'ballooning/settings/',
			providesTags: ['BallooningSettings'],
		}),
		updateBallooningSettings: builder.mutation<
			BallooningSettings,
			Partial<Omit<BallooningSettings, 'modified_time'>>
		>({
			query: (body) => ({ url: 'ballooning/settings/', method: 'PATCH', body }),
			invalidatesTags: ['BallooningSettings'],
		}),
	}),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
	useGetBallooningProjectsQuery,
	useLazyExportBallooningProjectToImageQuery,
	useGetBallooningProjectQuery,
	useUpdateBallooningProjectMutation,
	useGetBalloonsByProjectQuery,
	useCreateBallooningProjectMutation,
	useCreateBalloonMutation,
	useUpdateBalloonMutation,
	useDeleteBalloonMutation,
	useSetupInspectionPlanMutation,
	useResetInspectionPlanMutation,
	useGetBallooningProjectByLineItemQuery,
	useLazyGetBallooningProjectByLineItemQuery,
	useValidateBallooningChangesMutation,
	useRefreshPreviousOrdersMutation,
	useLazySearchReferenceProjectsQuery,
	useGenerateBalloonsMutation,
	useCancelGenerationMutation,
	useTraceViewsMutation,
	useGetGenerationJobQuery,
	useApproveBalloonsMutation,
	useDevUploadDrawingMutation,
	useGetKeyenceByProductionMethodQuery,
	useGetProductionMethodsQuery,
	useCreateKeyenceByProductionMethodMutation,
	useUpdateKeyenceByProductionMethodMutation,
	useDeleteKeyenceByProductionMethodMutation,
	useGetBallooningSettingsQuery,
	useUpdateBallooningSettingsMutation,
} = extendedApiSlice;
